import api from '../services/api';
import { initialData } from './state';
import { parseFormFieldsProperties } from '../helpers/utils';

export default {
  updateSettings({ commit }, payload) {
    commit('updateSettings', payload);
  },
  getUserInfo({ commit }) {
    return Promise.all([api.getUserInfo(), api.getDashboardUserInfo()])
      .then((res) => {
        const user = res[0].data;
        const dashboardUser = res[1].data;
        user['loginType'] = dashboardUser['loginType'];
        commit('updateUserInfo', user);
        return user;
      });
  },
  getSideNavItems({ commit }) {
    return api.getSideNavItems().then((res) => {
      commit('updateSideNavItems', res.data);
      return res.data;
    });
  },
  setActiveNavItem({ commit }, payload) {
    if (payload.route) {
      commit('updateActiveNavItem', {
        ...payload,
        properties: JSON.parse(payload.properties) || {},
      });
    } else {
      commit('updateActiveNavItem', { id: payload.id });
    }
  },
  resetDataGrid({ commit }) {
    commit('updateDataGrid', initialData().dataGrid);
  },
  toggleSideNavVisibility({ commit }) {
    commit('toggleSideNavVisibility');
  },
  async getCaseType(_context, id) {
    const res = await api.getCaseType(id);
    return res.data;
  },
  async getCaseCategory(_context, id) {
    const res = await api.getCaseCategory(id);
    return res.data;
  },
  getDataGridConfigurations({ commit, getters }, payload) {
    const gridId = payload || getters.activeMenuItem.gridId;
    if (gridId) {
      commit('updateDataGridItems', {
        items: [],
        total: 0,
      });
      commit('updateDataGridEndPointUrl', null);
      commit('updateDataGridFilters', []);
      commit('updateDataGridActions', []);
      commit('updateDataGridColumns', []);
      commit('updateDataGridSkipCastingKeys', []);
      commit('updateDataGridSkipCastingEnabled', false);
      return api.getDataGridConfigurations(gridId).then((res) => {
        commit('updateDataGridEndPointUrl', res.data.endPointUrl);
        commit('updateDataGridFilters', res.data.filters);
        commit('updateDataGridActions', res.data.actions);
        commit('updateDataGridSkipCastingKeys', res.data.skipCastingKeys);
        commit('updateDataGridSkipCastingEnabled', res.data.skipCastingEnabled);
        commit('updateDataGridColumns', res.data.columns);
      });
    }
    return Promise.resolve();
  },
  getDataGridItems({ commit, state }) {
    const endPointUrl = state.dataGrid.endPointUrl;
    if (endPointUrl) {
      commit('cancelDataGridHttpRequests');
      const query = {
        ...state.dataGrid.searchQuery.fields,
        ...state.dataGrid.searchQuery.filters,
        ...state.dataGrid.searchQuery.paging,
        ...state.dataGrid.searchQuery.sorting,
      };
      return api
        .getDataGridItems(endPointUrl, query, (callback) => {
          commit('addDataGridHttpRequestCancelCallback', callback);
        })
        .then((res) => {
          commit('updateDataGridItems', {
            items: res.data.list,
            total: res.data.totalCount,
          });
          return res;
        })
        .catch((error) => {
          return error;
        });
    }
    return Promise.resolve();
  },
  sortChange({ commit }, payload) {
    commit('updateDataGridSorting', payload);
  },
  pageChange({ commit }, payload) {
    commit('updateDataGridPageIndex', payload);
  },
  pageSizeChange({ commit }, payload) {
    commit('updateDataGridPageSize', payload);
  },
  searchChange({ commit }, payload) {
    commit('updateDataGridSearchQuery', payload);
  },
  updateAppLoader({ commit }, payload) {
    commit('updateAppLoader', payload);
  },
  updateOverlayLoader({ commit }, payload) {
    commit('updateOverlayLoader', payload);
  },
  updateBreadcrumbs({ commit }, payload) {
    commit('updateBreadcrumbs', payload);
  },
  clearBreadcrumbsData({ commit }) {
    commit('clearBreadcrumbsData');
  },
  getDashboardChartData(context, payload) {
    return api.getDashboardChartData(payload).then(res => res.data);
  },
  getDashboards() {
    return api.getDashboards().then(res => res.data);
  },
  getDynamicForms({ commit }) {
    return api.getDynamicForms().then((forms) => {
      commit('updateDynamicForms', forms.data);
      return forms.data;
    });
  },
  getDynamicForm({ state, commit }, { formId, withoutParse = false }) {
    return api.getDynamicForm(formId).then((form) => {
      commit(
        'updateDynamicForms',
        state.dynamicForms.map(f => (f.id === formId ? form.data : f))
      );
      return {
        ...form.data,
        fields: withoutParse ? form.data.fields : parseFormFieldsProperties(form.data.fields),
      };
    });
  },
  getDynamicFormFields({ state, dispatch, commit }, formId) {
    let selectedForm = state.dynamicForms.find(form => form.id === formId);
    let getAllDynamicForms = Promise.resolve([selectedForm]);
    if (!selectedForm) {
      getAllDynamicForms = dispatch('getDynamicForms');
    }
    return getAllDynamicForms.then((forms) => {
      const form = forms.find(form => form.id === formId);

      return api.getDynamicFormFields(formId).then((fields) => {
        selectedForm = {
          ...form,
          fields: parseFormFieldsProperties(fields.data),
        };
        commit('updateDynamicForm', selectedForm);

        return selectedForm;
      });
    });
  },
  addDynamicForm({ state, commit }, dynamicForm) {
    return api.addDynamicForm(dynamicForm).then((res) => {
      commit('updateDynamicForms', [...state.dynamicForms, res.data]);
    });
  },
  editDynamicForm({ state, commit }, dynamicForm) {
    return api.editDynamicForm(dynamicForm, dynamicForm.id).then(() => {
      commit(
        'updateDynamicForms',
        state.dynamicForms.map(form => (form.id === dynamicForm.id ? dynamicForm : form))
      );
    });
  },
  deleteDynamicForm({ state, commit }, formId) {
    commit(
      'updateDynamicForms',
      state.dynamicForms.filter(form => form.id !== formId)
    );
  },
  getDynamicGrids({ commit }) {
    return api.getDynamicGrids().then((res) => {
      commit('updateDynamicGrids', res.data);
    });
  },
  getDynamicGridsHasForms({ commit }) {
    return api.getDynamicGridsHasForms().then((res) => {
      commit('updateDynamicGrids', res.data);
    });
  },
  getDynamicGrid({ state, commit }, dynamicGrid) {
    return api.getDynamicGrid(dynamicGrid).then((res) => {
      commit(
        'updateDynamicGrids',
        state.dynamicGrids.map(grid => (grid.id === res.data.id ? res.data : grid))
      );
    });
  },
  addDynamicGrid({ state, commit }, dynamicGrid) {
    return api.addDynamicGrid(dynamicGrid).then((res) => {
      commit('updateDynamicGrids', [...state.dynamicGrids, res.data]);
      return res.data;
    });
  },
  getPickLists({ state, commit }) {
    if (state.pickLists && state.pickLists.length) return Promise.resolve(state.pickLists);
    return api.getPickLists().then((res) => {
      commit('updatePickLists', res.data);
    });
  },
  getRequests(context, query) {
    return api.getRequests(query);
  },
  getCaseTypeRequestTypes(_context, payload) {
    return api.getCaseTypeRequestTypes(payload)
      .then(r => r.data);
  },
  getDeceasedAndHeirsData(context, payload) {
    return api.getDeceasedAndHeirsData(payload);
  },
  getDeceasedAndHeirsDataForDefendant(context, payload) {
    return api.getDeceasedAndHeirsDataForDefendant(payload);
  },
  validateReportNumber(context, reportNumber) {
    return api.validateReportNumber(reportNumber)
      .then((res) => {
        let data = res?.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = {};
          }
        }
        const normalizedData = data?.data || data || {};
        return {
          ...res,
          data: normalizedData,
        };
      });
  },
  getRealEstateDeedInfo(context, payload) {
    return api.getRealEstateDeedInfo(payload);
  },
  getRealEstateDeedsInfoByOwnerId(context, payload) {
    return api.getRealEstateDeedsInfoByOwnerId(payload);
  },
  getRealEstateContractDetails(context, payload) {
    return api.getRealEstateContractDetails(payload);
  },
  getQaimConfg({ state, commit }) {
    // return api.getQaimConfig();
    return api.getQaimConfig().then((res) => {
      commit('updateQaimConfig', res.data);
    });
  },
  handleAttachments(context, payload) {
    return new Promise((resolve) => {
      const requestObjectKey = context.rootGetters['LawsuitModule/requestObjectKey'];
      const lawsuitState = context.rootState['LawsuitModule'];
      if (payload.attachments.length == 0 && payload.fileCategory != undefined) {
        context.commit('LawsuitModule/updateLawsuitRequestAttachments',
          [...lawsuitState.lawsuitRequest.requestAttachments.filter(a => a.fileCategory !== payload.fileCategory)], { root: true });
        resolve(true);
      }
      if (!requestObjectKey) {
        context.commit('LawsuitModule/updateLawsuitRequestAttachments',
          [...lawsuitState.lawsuitRequest.requestAttachments.filter(a => a.fileCategory !== payload.fileCategory), ...(payload.attachments || [])], { root: true });
        resolve(true);
      } else {
        const filteredContractFiles = Object.values(lawsuitState.attachments.data)
          .filter(c => c.category === payload.fileCategory);
        if (filteredContractFiles && filteredContractFiles.length > 0) {
          filteredContractFiles.forEach((filteredContract) => {
            context.dispatch('LawsuitModule/deleteAttachment', {
              objectKey: filteredContract.objectKey,
              attachmentKey: filteredContract.objectKey,
            }, { root: true });
          });
        }
        payload.attachments.forEach((attachment) => {
          if (requestObjectKey) {
            if (attachment.content || attachment.sharePointId) {
              context.dispatch('LawsuitModule/uploadBase64Attachment', attachment, { root: true })
                .then((res) => {
                  if (res) {
                    let attachmentData = {
                      name: attachment.fileName,
                      category: attachment.fileCategory,
                      categoryName: attachment.categoryName,
                      fileUrl: attachment.fileUrl,
                      fileObject: attachment.fileObject,
                      fileType: attachment.fileType,
                      objectKey: res.data,
                      isAttachmentAudited: true,
                    };
                    const attachmentType = lawsuitState.lawsuitRequest.attachmentTypes.find(a => a.id == attachment.fileCategory) || {};
                    if (Object.hasOwnProperty.call(attachmentType, 'isRequired')) {
                      attachmentData.isRequired = attachmentType.isRequired;
                    }
                    context.commit('LawsuitModule/updateAttachments', { [attachmentData.objectKey]: attachmentData }, { root: true });
                    context.dispatch('LawsuitModule/updateRequestAttachmentAuditField', {
                      requestAttachmentObjectKey: res.data,
                      isAttachmentAudited: true,
                    }, { root: true });
                    resolve(true);
                  } else {
                    resolve(false);
                  }
                })
                .catch(() => {
                  resolve(false);
                });
            } else {
              resolve(false);
            }
          }
        });
      }
    });
  },
  handleDeleteAttachmentsByCategory(context, fileCategory) {
    return new Promise((resolve) => {
      const requestObjectKey = context.rootGetters['LawsuitModule/requestObjectKey'];
      const lawsuitState = context.rootState['LawsuitModule'];
      const filteredContractFiles = Object.values(lawsuitState.attachments.data)
        .filter(c => c.category === fileCategory);

      if (requestObjectKey) {
        if (filteredContractFiles && filteredContractFiles.length > 0) {
          filteredContractFiles.forEach((filteredContract) => {
            context.dispatch('LawsuitModule/deleteAttachment', {
              objectKey: filteredContract.objectKey,
              attachmentKey: filteredContract.objectKey,
            }, { root: true });
          });
          context.commit('LawsuitModule/setAttachments',
            [...Object.values(lawsuitState.attachments.data)
              .filter(c => c.category !== fileCategory)], { root: true });
        }
        resolve(false);
      } else {
        if (fileCategory) {
          context.commit('LawsuitModule/updateLawsuitRequestAttachments',
            [...lawsuitState.lawsuitRequest.requestAttachments.
              filter(a => a.fileCategory !== fileCategory)], { root: true });
          resolve(true);
        } else {
          resolve(false);
        }
      }
    });
  },
  getNafathOtp(_context, payload) {
    return api.getNafathOtp(payload);
  },
  checkNafathOtpStatus(_context, payload) {
    return api.checkNafathOtpStatus(payload);
  },
  getRecaptchaConfig(_context) {
    return api.getRecaptchaConfig();
  },
};